import { AclDisplayValue, PositionsAclSidecar, TypedRedactionObject } from './types';

const DENIED_TEXT = 'Restricted';
const UNAVAILABLE_TEXT = 'Unavailable';
const SECRET_FRAGMENTS = [
  'access_token',
  'api_key',
  'api_secret',
  'credential',
  'password',
  'refresh_token',
  'secret',
  'token',
  'totp',
];

export function isTypedRedactionObject(value: unknown): value is TypedRedactionObject {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return candidate.status === 'redacted' && typeof candidate.reason === 'string';
}

export function isSecretLikeKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return SECRET_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

export function toAclDisplayValue(value: unknown): AclDisplayValue {
  if (isTypedRedactionObject(value)) {
    return {
      state: 'redacted',
      text: DENIED_TEXT,
      ariaLabel: 'Value restricted by access policy',
      sortValue: null,
      exportValue: 'REDACTED',
      chartValue: null,
    };
  }
  if (value === undefined || value === null) {
    return {
      state: 'unavailable',
      text: UNAVAILABLE_TEXT,
      ariaLabel: 'Value unavailable',
      sortValue: null,
      exportValue: 'UNAVAILABLE',
      chartValue: null,
    };
  }
  if (typeof value === 'number') {
    return {
      state: 'visible',
      text: String(value),
      ariaLabel: String(value),
      sortValue: value,
      exportValue: value,
      chartValue: value,
    };
  }
  if (typeof value === 'string') {
    return {
      state: 'visible',
      text: value,
      ariaLabel: value,
      sortValue: value,
      exportValue: value,
      chartValue: null,
    };
  }
  return {
    state: 'visible',
    text: String(value),
    ariaLabel: String(value),
    sortValue: String(value),
    exportValue: String(value),
    chartValue: null,
  };
}

export function summarizeCoverage(sidecar?: PositionsAclSidecar | null): string | null {
  if (!sidecar?.partial) {
    return null;
  }
  if (sidecar.coverage?.scope === 'allowed_accounts_only') {
    return 'Partial view: allowed accounts only';
  }
  return 'Partial view';
}

export function sanitizeAclStateForClientCache(value: unknown): unknown {
  if (isTypedRedactionObject(value)) {
    return { status: 'redacted', reason: value.reason };
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeAclStateForClientCache(entry));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
      (safe, [key, entry]) => {
        if (!isSecretLikeKey(key)) {
          safe[key] = sanitizeAclStateForClientCache(entry);
        }
        return safe;
      },
      {},
    );
  }
  return value;
}

export function createSafeChartPoint(value: unknown): number | null {
  return toAclDisplayValue(value).chartValue;
}

export function createSafeExportValue(value: unknown): string | number | null {
  return toAclDisplayValue(value).exportValue;
}

export function createSafeSortValue(value: unknown): string | number | null {
  return toAclDisplayValue(value).sortValue;
}
